import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useEffect, useState } from 'react'
import { useClient, useLoading, useQuery } from '@based/sdk/react'
import defaultProjectPicture from '@app/assets/defaultProjectPicture.png'
import { match } from 'ts-pattern'
import { SearchMap } from '../sections/SearchMap'
import { useLocation } from 'wouter'
import { DiscoveryCrawlerPaginationFooter } from '@app/components/blocks/searchGrid/DiscoveryCrawlerPaginationFooter'
import {
  useDiscoveryCrawlerPage,
  usePageIndex,
} from '@app/util/hooks/useDiscoveryCrawlerPage'
import { pages } from '@app/util/links'
import { PlotFilter } from 'functions/app/plot/list/filters'
import { PlotSearchGrid } from '../sections/PlotSearchGrid'
import { ListPlotModel } from 'functions/app/plot/list'
import { PlotFilterDrawer } from '../sections/PlotFilterDrawer'
import {
  useQueryStates,
  parseAsArrayOf,
  parseAsInteger,
  parseAsBoolean,
  parseAsStringLiteral,
} from 'nuqs'
import { PLOT_APPLICATION_STATUSES } from 'schema/plot'
import { Empty } from '@app/components/blocks/empty/Empty'
import FindLocationImage from '@app/assets/images/find-location.png'

export const DiscoverPlots = ({
  view,
  searchTerm,
  onResetSearch,
  filtersOpen,
  onFiltersClose,
}: {
  view: 'grid' | 'map'
  searchTerm: string
  onResetSearch: () => void
  filtersOpen: boolean
  onFiltersClose: () => void
}) => {
  const PAGE_SIZE = 15
  const t = useTranslations()
  const translations = t.app.discoveryPage.tabs.plots
  const commonTranslations = t.app.common
  const requestedPage = usePageIndex()
  const fetchTop = requestedPage * PAGE_SIZE
  const [total, setTotal] = useState(0)
  const [plots, setPlots] = useState<ListPlotModel[]>([])
  const [top, setTop] = useState(fetchTop)
  const [mounted, setMounted] = useState(false)
  const client = useClient()
  const isLoading = useLoading()
  const [_location, navigate] = useLocation()

  const [filterParams, setFilterParams] = useQueryStates({
    applicationStatus: parseAsArrayOf(
      parseAsStringLiteral(Object.values(PLOT_APPLICATION_STATUSES)),
    ),
    byCrowdbuilding: parseAsBoolean,
    province: parseAsArrayOf(parseAsInteger),
  })

  // Wrapper to match PlotFilterDrawer's expected setFilters signature
  const setFilters = useCallback(
    (newFilters: PlotFilter) => {
      void setFilterParams({
        applicationStatus: newFilters.applicationStatus ?? null,
        byCrowdbuilding: newFilters.byCrowdbuilding ?? null,
        province: newFilters.province ?? null,
      })
    },
    [setFilterParams],
  )

  // useQuery is for SSR and client side queries will be managed by client.query
  const dataServerQuery = useQuery('list-plot', {
    top: fetchTop,
    searchTerm: searchTerm,
    filters: filterParams,
  })

  const serverPlots: ListPlotModel[] = dataServerQuery.data ?? []

  const renderedPlots = mounted ? plots : serverPlots
  const { nextPageHref } = useDiscoveryCrawlerPage({
    hasMore: serverPlots.length >= fetchTop,
  })

  const query = useCallback(
    (topParam: number) => {
      const close = client
        .query('list-plot', {
          searchTerm: searchTerm,
          top: topParam,
          filters: filterParams,
        })
        .subscribe((plots) => {
          setPlots(plots)
          setMounted(true)
        })
      return () => close()
    },
    [client, searchTerm, filterParams],
  )
  const countQuery = useCallback(() => {
    const close = client
      .query('list-plot', {
        searchTerm: searchTerm,
        filters: filterParams,
        count: true,
      })
      .subscribe((r) => setTotal(r.count))
    return () => close()
  }, [client, searchTerm, filterParams])

  useEffect(() => {
    setTop(fetchTop)
    const cleanupCount = countQuery()
    const cleanupQuery = query(fetchTop)
    return () => {
      cleanupCount()
      cleanupQuery()
    }
  }, [searchTerm, filterParams, fetchTop, countQuery, query])

  useEffect(() => {
    if (top === fetchTop) {
      return
    }
    const cleanup = query(top)
    return () => cleanup()
  }, [top, query, fetchTop])

  return (
    <>
      <PlotFilterDrawer
        open={filtersOpen}
        onClose={onFiltersClose}
        setFilters={setFilters}
        filters={filterParams}
      />

      {renderedPlots.length === 0 && !isLoading ? (
        <Empty
          title={translations.emptyTitle}
          imageSrc={FindLocationImage}
          style={{ marginTop: '4rem' }}
        >
          {translations.emptyDescription}
        </Empty>
      ) : (
        match(view)
          .with('grid', () => (
            <PlotSearchGrid
              items={renderedPlots?.map((plot) => ({
                slug: plot.slug,
                to: pages.plot(plot.slug),
                image: plot.media?.[0] ?? defaultProjectPicture,
                title: plot.name,
                description: plot.description,
                address: plot.location.address,
                tagText:
                  commonTranslations.plotApplicationStatus[
                    plot.applicationStatus
                  ],
                tagVariant:
                  plot.applicationStatus === 'applicationOpen'
                    ? 'success'
                    : 'neutral',
              }))}
              totalItems={total}
              title={translations.title}
              subtitle={translations.subTitle}
              isLoading={isLoading}
              searchTerm={searchTerm}
              loadMore={() => setTop((prev) => prev + PAGE_SIZE)}
              onResetSearch={onResetSearch}
              onNavigate={(key) => navigate(pages.plot(key))}
              footer={
                <DiscoveryCrawlerPaginationFooter nextPageHref={nextPageHref} />
              }
            />
          ))
          .with('map', () => (
            <SearchMap
              items={plots.map((plot) => ({
                id: plot.slug,
                image: plot.media?.[0]?.src ?? defaultProjectPicture,
                title: plot.name,
                description: plot.description,
                address: plot.location.address,
                lat: plot.location.geo.lat,
                long: plot.location.geo.long,
              }))}
              totalItems={total}
              title={translations.title}
              subtitle={translations.subTitle}
              isLoading={isLoading}
              searchTerm={searchTerm}
              loadMore={() => setTop((prev) => prev + PAGE_SIZE)}
              onResetSearch={onResetSearch}
            />
          ))
          .exhaustive()
      )}
    </>
  )
}
